import React from 'react'
import { InferGetServerSidePropsType, Redirect } from 'next'
import { useRouter } from 'next/router'
import { useBreakpointValue } from '@hub/design-system-base'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import {
  NationalStaticData,
  fetchNationalStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { makeUnscaledImageUrl } from '@scentregroup/shared/hub-components/cloudinary-image/helpers/make-image-url'
import { NewsArticle, WithContext } from 'schema-dts'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import {
  ContentDetail,
  getContentDetail,
} from '../../../lib/get-content-detail'
import { getDefaultNationalMetaData } from '../../../../../src/shared/helpers/get-default-national-metadata'
import { StoryContentDetail } from '../../../components/page-templates/content-detail/story'
import { META_FALLBACK_IMAGE } from '../../../../../src/shared/constants'
import { shouldRedirectExpiredContent } from '../../../lib/redirect-expired-content'
import { toDateString } from '../../../lib/to-date-string'

export interface INationalStoryPageProps {
  country: ReturnType<typeof localeToCountry>
  story: ContentDetail['story']
  headerStaticData: NationalStaticData
}

function getRedirectForStoryWithMismatchedSlug(
  ctx: GetServerSidePropsContext<{ id: string; slug: string }>,
  story: ContentDetail['story']
): Redirect | null {
  if (!story.slug) {
    return null
  }

  if (story.slug !== ctx.params?.slug) {
    const e = encodeURIComponent
    return {
      destination: `/story/${e(story.sys.id)}/${e(story.slug)}`,
      permanent: true,
    }
  }
  return null
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{ id: string; slug: string }>
): Promise<GetServerSidePropsResult<INationalStoryPageProps>> => {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const id = ctx.params?.id
  if (!id) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()

  const [headerStaticData, story] = await Promise.all([
    fetchNationalStaticData(apollo, { country }),
    getContentDetail(apollo, 'story', { id }),
  ])

  if (!story) {
    return {
      notFound: true,
    }
  }

  const redirect = shouldRedirectExpiredContent(story.hideAt, '/')

  if (redirect) {
    return redirect
  }

  const redirectObj = getRedirectForStoryWithMismatchedSlug(ctx, story)

  if (redirectObj) {
    return { redirect: redirectObj }
  }

  return {
    props: {
      country,
      headerStaticData,
      story,
    },
  }
}

const StoryDetailPage: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = props => {
  const { country, story, headerStaticData } = props
  const headerHasBottomMargin = useBreakpointValue({ base: false, sm: true })
  const router = useRouter()

  const metaTitle = story.title
  const metaDescription = getDefaultNationalMetaData({
    country,
    type: 'default',
  }).description
  const metaCanonical = router.asPath

  const metaData = {
    title: metaTitle,
    description: metaDescription,
    canonical: metaCanonical,
    openGraph: {
      url: metaCanonical,
      title: metaTitle,
      description: metaDescription,
      image: story.heroImageSet?.master?.url || META_FALLBACK_IMAGE,
    },
  }

  const schema = story.heroImageSet?.master
    ? getNewsArticleStructuredDataFromStory(
        story,
        router.domainLocales?.find(l => l.defaultLocale === router.locale)
          ?.domain
      )
    : undefined

  return (
    <>
      <SGLinkedData item={schema} />
      <Layout
        country={country}
        centre={undefined}
        header={{
          variant: 'national',
          data: headerStaticData,
          hasBottomMargin: headerHasBottomMargin,
        }}
        metaData={{ ...metaData, generateCanonical: true }}
      >
        <StoryContentDetail content={story} />
      </Layout>
    </>
  )
}

function getNewsArticleStructuredDataFromStory(
  story: ContentDetail['story'],
  domain: string | undefined
): WithContext<NewsArticle> {
  const image = story.heroImageSet?.master?.url || META_FALLBACK_IMAGE

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: story.title,
    image: [makeUnscaledImageUrl(image)],
    datePublished: toDateString(story.sys.firstPublishedAt),
    dateModified: toDateString(story.sys.publishedAt),
    ...(domain
      ? {
          author: {
            '@type': 'Organization',
            name: 'Westfield',
            url: `https://${domain}/`,
          },
        }
      : {}),
  }
}

export default StoryDetailPage
